import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import { FormData } from '../../../types/transaction';

interface Negotiator {
  id: string;
  firstname: string;
  lastname: string;
  role: string;
  status: string;
  archived: boolean;
  departure_date: string | null;
  agencies: string[];
}

interface NegotiatorsFormProps {
  formData: FormData;
  onChange: (data: FormData) => void;
  readOnly?: boolean;
}

export default function NegotiatorsForm({ formData, onChange, readOnly = false }: NegotiatorsFormProps) {
  const [negotiators, setNegotiators] = useState<Negotiator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [directionId, setDirectionId] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);

  // Clé stable : évite de recharger la liste à chaque frappe dans le formulaire
  const selectedAgenciesKey = formData.agencies.map(a => a.name).filter(Boolean).join('||');

  useEffect(() => {
    if (selectedAgenciesKey) {
      loadNegotiators();
      loadDirectionId();
    } else {
      setNegotiators([]);
    }
  }, [selectedAgenciesKey]);

  // Une fois la liste chargée, on fige l'agence de chaque ligne dans le formulaire.
  // Sans cela, une ligne sans agence enregistrée affichait la 1re agence du négociateur
  // sans jamais l'enregistrer : à la réouverture elle « revenait » toujours sur celle-ci.
  useEffect(() => {
    if (readOnly || negotiators.length === 0) return;

    let hasChanges = false;
    const resolved = formData.negotiators.map(line => {
      if (!line.id) return line;

      const negotiator = negotiators.find(n => n.id === line.id);
      if (!negotiator) return line;

      // Agences du négociateur retenues sur cette transaction
      const candidates = negotiator.agencies;

      // Agence enregistrée toujours valable : rien à faire
      if (line.agency && candidates.includes(line.agency)) return line;

      // Une seule agence possible : on la fige dans le formulaire
      if (candidates.length === 1) {
        if (line.agency === candidates[0]) return line;
        hasChanges = true;
        return { ...line, agency: candidates[0] };
      }

      // Ambigu (ou agence devenue invalide) : l'utilisateur doit choisir
      if (line.agency) {
        hasChanges = true;
        return { ...line, agency: '' };
      }
      return line;
    });

    if (hasChanges) {
      onChange({ ...formData, negotiators: resolved });
    }
    // Volontairement déclenché sur le chargement de la liste uniquement :
    // dépendre de formData relancerait l'effet à chaque saisie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [negotiators, readOnly]);

  const loadDirectionId = async () => {
    try {
      // Trouver la société de la première agence
      const firstAgency = formData.agencies[0]?.name;
      if (!firstAgency) return;

      const { data: agencyData } = await supabase
        .from('agencies')
        .select('company')
        .eq('name', firstAgency)
        .single();

      if (!agencyData) return;

      // Récupérer l'ID de la Direction correspondante
      const { data: directionData, error: directionError } = await supabase
        .from('negotiators')
        .select('id')
        .eq('firstname', 'DIRECTION')
        .eq('lastname', 'EKYO')
        .single();

      if (directionError) {
        // Si DIRECTION EKYO n'existe pas, on le crée
        const { data: newDirection, error: createError } = await supabase
          .from('negotiators')
          .insert({
            firstname: 'DIRECTION',
            lastname: 'EKYO',
            role: 'directeur',
            status: 'directeur',
            annual_objective: 0,
            archived: false
          })
          .select()
          .single();

        if (createError) throw createError;
        if (newDirection) setDirectionId(newDirection.id);
      } else if (directionData) {
        setDirectionId(directionData.id);
      }
    } catch (err) {
      console.error('Error loading/creating DIRECTION EKYO:', err);
      setError('Erreur lors du chargement des données de direction');
    }
  };

  const loadNegotiators = async () => {
    try {
      setLoading(true);
      const selectedAgencies = formData.agencies
        .map(a => a.name)
        .filter(Boolean);

      if (selectedAgencies.length === 0) return;

      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      const sixMonthsAgoStr = sixMonthsAgo.toISOString().split('T')[0];

      const { data, error } = await supabase
        .from('negotiators')
        .select(`
          id,
          firstname,
          lastname,
          role,
          status,
          archived,
          departure_date,
          negotiator_agencies!inner(agency)
        `)
        .or(`archived.eq.false,and(archived.eq.true,departure_date.gte.${sixMonthsAgoStr})`)
        .in('negotiator_agencies.agency', selectedAgencies)
        .order('lastname')
        .order('firstname');

      if (error) throw error;

      // Grouper les négociateurs par ID et collecter leurs agences
      const negotiatorsMap = new Map<string, Negotiator>();
      
      data?.forEach(n => {
        // Filtrer DIRECTION EKYO sauf si l'agence "Siège" est sélectionnée
        if (n.firstname === 'DIRECTION' && n.lastname === 'EKYO' && !selectedAgencies.includes('SIEGE')) {
          return;
        }

        const agencies = n.negotiator_agencies
          .map((na: any) => na.agency)
          .filter((agency: string) => selectedAgencies.includes(agency));

        if (!negotiatorsMap.has(n.id)) {
          negotiatorsMap.set(n.id, {
            id: n.id,
            firstname: n.firstname,
            lastname: n.lastname,
            role: n.role,
            status: n.status,
            archived: n.archived,
            departure_date: n.departure_date,
            agencies: agencies
          });
        } else {
          const existing = negotiatorsMap.get(n.id)!;
          existing.agencies = [...new Set([...existing.agencies, ...agencies])];
        }
      });

      setNegotiators(Array.from(negotiatorsMap.values()));
    } catch (err) {
      console.error('Error loading negotiators:', err);
      setError('Erreur lors du chargement des négociateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNegotiator = () => {
    if (!readOnly) {
      onChange({
        ...formData,
        negotiators: [
          ...formData.negotiators,
          { id: '', role: 'vendeur_apporteur', percentage: 0, name: '', agency: '' }
        ]
      });
    }
  };

  const handleRemoveNegotiator = (index: number) => {
    if (!readOnly) {
      const newNegotiators = formData.negotiators.filter((_, i) => i !== index);
      onChange({
        ...formData,
        negotiators: newNegotiators
      });
      validateNegotiators(newNegotiators);
    }
  };

  const validateNegotiators = (lines = formData.negotiators) => {
    const totalPercentage = lines.reduce((sum, n) => sum + n.percentage, 0);
    const allNegotiatorsSelected = lines.every(n => n.id);
    const isValid = totalPercentage === 100 && allNegotiatorsSelected;
    setIsValid(isValid);
    return isValid;
  };

  // Lignes dont l'agence reste à préciser (négociateur présent dans plusieurs
  // agences de la transaction, sans agence enregistrée)
  const linesNeedingAgency = formData.negotiators.filter(line => {
    if (!line.id || line.agency) return false;
    const negotiator = negotiators.find(n => n.id === line.id);
    return (negotiator?.agencies.length || 0) > 1;
  }).length;

  const shouldTransferToDirection = (negotiator: Negotiator) => {
    if (!negotiator.departure_date) return false;
    
    const departureDate = new Date(negotiator.departure_date);
    const sixMonthsAfterDeparture = new Date(departureDate);
    sixMonthsAfterDeparture.setMonth(departureDate.getMonth() + 6);
    
    return new Date() > sixMonthsAfterDeparture;
  };

  // Créer une liste d'options avec les négociateurs dupliqués par agence si nécessaire
  const getNegotiatorOptions = () => {
    const options: Array<{
      value: string;
      label: string;
      negotiator: Negotiator;
      agency?: string;
      disabled: boolean;
    }> = [];

    negotiators.forEach(n => {
      const isArchived = n.archived && n.departure_date;
      const departureDate = isArchived ? new Date(n.departure_date).toLocaleDateString('fr-FR') : null;
      const shouldTransfer = shouldTransferToDirection(n);

      // Si le négociateur est dans plusieurs agences sélectionnées, créer une option par agence
      if (n.agencies.length > 1) {
        n.agencies.forEach(agency => {
          options.push({
            value: `${n.id}|${agency}`,
            label: `${n.lastname} ${n.firstname} (${agency}) - ${n.role} - ${n.status}${isArchived ? ` (Départ le ${departureDate})` : ''}`,
            negotiator: n,
            agency: agency,
            disabled: shouldTransfer
          });
        });
      } else if (n.agencies.length === 1) {
        // Si une seule agence, utiliser le format avec l'agence pour garantir qu'elle soit sauvegardée
        options.push({
          value: `${n.id}|${n.agencies[0]}`,
          label: `${n.lastname} ${n.firstname} (${n.role} - ${n.status})${isArchived ? ` (Départ le ${departureDate})` : ''}`,
          negotiator: n,
          agency: n.agencies[0],
          disabled: shouldTransfer
        });
      } else {
        // Pas d'agence (ne devrait pas arriver normalement)
        options.push({
          value: n.id,
          label: `${n.lastname} ${n.firstname} (${n.role} - ${n.status})${isArchived ? ` (Départ le ${departureDate})` : ''}`,
          negotiator: n,
          agency: undefined,
          disabled: shouldTransfer
        });
      }
    });

    return options;
  };

  if (loading && formData.agencies.some(a => a.name)) {
    return <div>Chargement des négociateurs...</div>;
  }

  if (error) {
    return <div className="text-red-500">{error}</div>;
  }

  const negotiatorOptions = getNegotiatorOptions();

  return (
    <div className="space-y-4">
      {!readOnly && (
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-medium text-gray-900">Négociateurs</h3>
          <button
            type="button"
            onClick={handleAddNegotiator}
            className="text-sm text-blue-600 hover:text-blue-700"
          >
            + Ajouter un négociateur
          </button>
        </div>
      )}

      {formData.negotiators.map((negotiator, index) => {
        // Calculer la valeur actuelle du select en fonction du négociateur sélectionné
        const currentNegotiator = negotiators.find(n => n.id === negotiator.id);
        let selectValue = '';
        
        // Le négociateur est-il rattaché à plusieurs agences de cette transaction ?
        const isMultiAgency = (currentNegotiator?.agencies.length || 0) > 1;
        // Agence enregistrée mais absente de la liste (agence retirée de la transaction)
        const hasOrphanAgency =
          !!negotiator.agency &&
          !!currentNegotiator &&
          !currentNegotiator.agencies.includes(negotiator.agency);

        if (negotiator.id) {
          if (negotiator.agency && !hasOrphanAgency) {
            // Agence explicitement enregistrée : on l'affiche telle quelle
            selectValue = `${negotiator.id}|${negotiator.agency}`;
          } else if (currentNegotiator && currentNegotiator.agencies.length === 1) {
            selectValue = `${currentNegotiator.id}|${currentNegotiator.agencies[0]}`;
          } else if (isMultiAgency) {
            // Plusieurs agences possibles et aucune enregistrée : on ne devine pas,
            // la ligne reste vide pour forcer un choix explicite (et enregistré).
            selectValue = '';
          } else {
            selectValue = negotiator.id;
          }
        }

        return (
          <div key={index} className="grid grid-cols-3 gap-4 items-center">
            <div>
              <select
                value={selectValue}
                onChange={(e) => {
                  if (!readOnly) {
                    const newNegotiators = [...formData.negotiators];
                    const selectedValue = e.target.value;
                    
                    // Parser la valeur pour extraire l'ID et l'agence
                    const [negotiatorId, agency] = selectedValue.includes('|') 
                      ? selectedValue.split('|') 
                      : [selectedValue, ''];
                    
                    const selectedNegotiator = negotiators.find(n => n.id === negotiatorId);
                    
                    // Si le négociateur est parti depuis plus de 6 mois, utiliser Direction
                    if (selectedNegotiator && shouldTransferToDirection(selectedNegotiator) && directionId) {
                      newNegotiators[index] = {
                        ...newNegotiators[index],
                        id: directionId,
                        name: 'DIRECTION EKYO',
                        agency: ''
                      };
                    } else if (selectedNegotiator) {
                      // Déterminer l'agence à utiliser
                      let finalAgency = agency;
                      
                      // Si pas d'agence dans la valeur mais le négociateur n'a qu'une agence, l'utiliser
                      if (!finalAgency && selectedNegotiator.agencies.length === 1) {
                        finalAgency = selectedNegotiator.agencies[0];
                      }
                      
                      // Si toujours pas d'agence mais qu'il n'y a qu'une seule agence sélectionnée, l'utiliser
                      if (!finalAgency && formData.agencies.length === 1 && formData.agencies[0].name) {
                        finalAgency = formData.agencies[0].name;
                      }
                      
                      newNegotiators[index] = {
                        ...newNegotiators[index],
                        id: negotiatorId,
                        name: `${selectedNegotiator.firstname} ${selectedNegotiator.lastname}`,
                        agency: finalAgency || ''
                      };
                    }
                    
                    onChange({ ...formData, negotiators: newNegotiators });
                    validateNegotiators(newNegotiators);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                required
                disabled={readOnly}
              >
                <option value="">Sélectionner un négociateur</option>
                {negotiatorOptions.map((option, optIndex) => (
                  <option 
                    key={optIndex}
                    value={option.value}
                    disabled={option.disabled}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <select
                value={negotiator.role}
                onChange={(e) => {
                  if (!readOnly) {
                    const newNegotiators = [...formData.negotiators];
                    newNegotiators[index] = {
                      ...newNegotiators[index],
                      role: e.target.value as 'apporteur' | 'vendeur' | 'vendeur_apporteur' | 'reco' | 'autre'
                    };
                    onChange({ ...formData, negotiators: newNegotiators });
                    validateNegotiators(newNegotiators);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                required
                disabled={readOnly}
              >
                <option value="vendeur">Vendeur</option>
                <option value="apporteur">Apporteur</option>
                <option value="vendeur_apporteur">Vendeur/Apporteur</option>
                <option value="reco">Reco</option>
                <option value="autre">Autre</option>
              </select>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex-1">
                <input
                  type="number"
                  value={negotiator.percentage}
                  onChange={(e) => {
                    if (!readOnly) {
                      const newNegotiators = [...formData.negotiators];
                      newNegotiators[index] = {
                        ...newNegotiators[index],
                        percentage: parseInt(e.target.value) || 0
                      };
                      onChange({ ...formData, negotiators: newNegotiators });
                      validateNegotiators(newNegotiators);
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  min="0"
                  max="100"
                  required
                  readOnly={readOnly}
                />
              </div>
              <span className="text-gray-500">%</span>

              {!readOnly && formData.negotiators.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveNegotiator(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  <X size={20} />
                </button>
              )}
            </div>
          </div>
        );
      })}

      {!readOnly && (
        <div className={`text-sm ${isValid ? 'text-green-600' : 'text-gray-500'}`}>
          Total: {formData.negotiators.reduce((sum, n) => sum + n.percentage, 0)}%
          {!isValid && (
            <span className="text-red-500 ml-2">
              (Le total doit être égal à 100% et tous les négociateurs doivent être sélectionnés)
            </span>
          )}
          {linesNeedingAgency > 0 && (
            <div className="text-amber-600 mt-1">
              {linesNeedingAgency > 1
                ? `${linesNeedingAgency} lignes concernent un négociateur présent dans plusieurs agences : sélectionnez l'agence à retenir.`
                : "Ce négociateur est présent dans plusieurs agences : sélectionnez l'agence à retenir."}
            </div>
          )}
        </div>
      )}
    </div>
  );
}